import {
  Box,
  Button,
  Checkbox,
  Container,
  FormControl,
  FormHelperText,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  TextField,
  FormControlLabel,
} from "@mui/material";
import React, { useState } from "react";
import BackBtn from "../../widgets/BackBtn";
import PageTitle from "../../widgets/PageTitle";
import Joi from "joi";
import axios from "axios";
import { toast } from "react-toastify";
import { getHeader, toastError } from "../../utils/util";
import { API_ENDPOINT } from "../../utils/constants";
import { LoadingButton } from "@mui/lab";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { GlassButton, GlassButtonLoading } from "../../themes/UiElements";

// Category options
const CATEGORIES = [
  "SIGN_UP",
  "SIGN_IN",
  "APPOINTMENT_BOOKING",
  "LEAD_GENERATION",
  "CONTACT_US",
  "CUSTOMER_SUPPORT",
  "SURVEY",
  "OTHER",
];

// Validation schema
const schema = Joi.object({
  name: Joi.string()
    .required()
    .min(3)
    .max(50)
    .regex(
      /^[a-zA-Z0-9 _-]+$/,
      "letters, numbers, spaces, underscores, and dashes"
    )
    .messages({
      "string.empty": "Name is required",
      "string.min": "Name should have at least 3 characters",
      "string.max": "Name should not exceed 50 characters",
      "string.pattern.base":
        "Name can only contain letters, numbers, spaces, underscores, and dashes",
    }),
  categories: Joi.array()
    .items(Joi.string().valid(...CATEGORIES))
    .min(1)
    .required()
    .messages({
      "array.min": "At least one category must be selected",
      "any.required": "Categories are required",
    }),
  isEndpointPowered: Joi.boolean().required(),
  endpoint: Joi.string().when("isEndpointPowered", {
    is: true,
    then: Joi.string().uri().required().messages({
      "string.empty":
        "Endpoint URL is required when endpoint powered is enabled",
      "string.uri": "Endpoint must be a valid URL",
    }),
    otherwise: Joi.string().allow("").optional(),
  }),
});

const CreateFlow = () => {
  const [formData, setFormData] = useState({
    name: "",
    categories: [],
    isEndpointPowered: false,
    endpoint: "",
  });
  const [creating, setCreating] = useState(false);
  const [errors, setErrors] = React.useState({});

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // Additional client-side validation for the name field
  const handleNameChange = (e) => {
    const { value } = e.target;
    // Only update if the input matches our allowed pattern or is empty
    if (value === "" || /^[a-zA-Z0-9 _-]*$/.test(value)) {
      setFormData((prev) => ({
        ...prev,
        name: value,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate the form
    const { error } = schema.validate(formData, { abortEarly: false });

    if (error) {
      const newErrors = {};
      error.details.forEach((detail) => {
        newErrors[detail.path[0]] = detail.message;
      });
      setErrors(newErrors);
      return;
    }

    // Clear errors if validation passes
    setErrors({});

    try {
      setCreating(true);
      const response = await axios.post(
        `${API_ENDPOINT}/api/create-flow`,
        formData,
        getHeader()
      );
      setFormData({
        name: "",
        categories: [],
        isEndpointPowered: false,
        endpoint: "",
      });
      toast.success(response.data.message);
    } catch (error) {
      toastError(error);
    } finally {
      setCreating(false);
    }
  };

  return (
    <Container sx={{ my: 3 }}>
      <BackBtn />
      <Container maxWidth="md">
        <PageTitle sx={{ mt: 1 }}>Create Flow</PageTitle>
        <Paper variant="outlined" sx={{ p: 3, mt: 3 }}>
          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Name"
              name="name"
              value={formData.name}
              onChange={handleNameChange}
              margin="normal"
              error={!!errors.name}
              helperText={errors.name}
              inputProps={{
                pattern: "^[a-zA-Z0-9 _-]*$",
                title:
                  "Only letters, numbers, spaces, underscores, and dashes are allowed",
              }}
            />

            <FormControl fullWidth margin="normal" error={!!errors.categories}>
              <InputLabel id="categories-label">Categories</InputLabel>
              <Select
                labelId="categories-label"
                id="categories"
                name="categories"
                multiple
                value={formData.categories}
                onChange={handleChange}
                input={<OutlinedInput label="Categories" />}
                renderValue={(selected) => selected.join(", ")}
              >
                {CATEGORIES.map((category) => (
                  <MenuItem key={category} value={category}>
                    <Checkbox
                      checked={formData.categories.indexOf(category) > -1}
                    />
                    <ListItemText primary={category} />
                  </MenuItem>
                ))}
              </Select>
              {errors.categories && (
                <FormHelperText>{errors.categories}</FormHelperText>
              )}
            </FormControl>

            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.isEndpointPowered}
                  onChange={handleChange}
                  name="isEndpointPowered"
                />
              }
              label="Endpoint Powered"
              sx={{ mt: 2 }}
            />

            {formData.isEndpointPowered && (
              <TextField
                fullWidth
                label="Endpoint URL"
                name="endpoint"
                value={formData.endpoint}
                onChange={handleChange}
                margin="normal"
                error={!!errors.endpoint}
                helperText={errors.endpoint}
                inputProps={{
                  type: "url",
                  placeholder: "https://example.com/api",
                }}
              />
            )}

            <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
              <GlassButtonLoading
                type="submit"
                variant="contained"
                color="primary"
                size="large"
                loading={creating}
                startIcon={<EditOutlinedIcon />}
              >
                Create Flow
              </GlassButtonLoading>
            </Box>
          </Box>
        </Paper>
      </Container>
    </Container>
  );
};

export default CreateFlow;
